import React, { useState, useRef } from 'react'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Button } from './button'
import { cn } from 'src/utils/style'

interface CollapsiblePanelProps {
  title: string | React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  className?: string
  headerClassName?: string
  contentClassName?: string
  icon?: React.ReactNode
  direction?: 'vertical' | 'horizontal'
  collapsedWidth?: string
  expandedWidth?: string
  collapseIcon?: React.ReactNode
  expandIcon?: React.ReactNode
  onToggle?: (isOpen: boolean) => void
  showBorder?: boolean
}

export const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  title,
  children,
  defaultOpen = true,
  className = '',
  headerClassName = '',
  contentClassName = '',
  icon,
  direction = 'vertical',
  collapsedWidth = '5rem',
  expandedWidth = '24rem',
  collapseIcon,
  expandIcon,
  onToggle,
  showBorder = true,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const contentRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    const newState = !isOpen
    setIsOpen(newState)
    onToggle?.(newState)
  }

  // Determine the appropriate icon based on direction and state
  const getToggleIcon = () => {
    if (direction === 'horizontal') {
      return isOpen
        ? collapseIcon || <ChevronLeftIcon className="h-4 w-4" />
        : expandIcon || <ChevronRightIcon className="h-4 w-4" />
    }
    return (
      <div
        className={`transition-transform duration-200 ease-in-out ${
          isOpen ? 'rotate-180' : 'rotate-0'
        }`}
      >
        {collapseIcon || <ChevronDownIcon className="h-4 w-4" />}
      </div>
    )
  }

  const containerClasses =
    direction === 'horizontal'
      ? `flex transition-all duration-300 ease-in-out ${className}`
      : `rounded-lg ${showBorder ? 'border border-border' : ''} bg-surface ${className}`

  const radialBgStyle = { background: 'radial-gradient(circle, var(--gray-100), transparent)' }
  const horizontalOpenHeaderClasses = cn(
    'flex cursor-pointer items-center justify-between p-4 duration-200',
    headerClassName,
  )
  const horizontalClosedHeaderClasses = cn(
    'flex cursor-pointer items-center justify-center p-4 duration-200',
    headerClassName,
  )

  const headerClasses =
    direction === 'horizontal'
      ? isOpen
        ? horizontalOpenHeaderClasses
        : horizontalClosedHeaderClasses
      : `flex cursor-pointer items-center justify-between p-4 transition-colors duration-200 hover:bg-surface-hover ${headerClassName}`

  return (
    <div
      className={containerClasses}
      style={direction === 'horizontal' ? { width: isOpen ? expandedWidth : collapsedWidth } : {}}
    >
      <div
        className={headerClasses}
        style={direction === 'horizontal' ? radialBgStyle : undefined}
        onClick={handleToggle}
      >
        {direction === 'horizontal' && !isOpen ? (
          <Button
            variant="unstyled"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              handleToggle()
            }}
          >
            {getToggleIcon()}
          </Button>
        ) : (
          <>
            <div className="flex items-center gap-2">
              {icon && (
                <div className="text-text-secondary transition-colors duration-200">{icon}</div>
              )}
              {typeof title === 'string' ? (
                <h3 className="text-lg font-semibold text-brand-primary">{title}</h3>
              ) : (
                title
              )}
            </div>
            <Button
              variant="ghost"
              shape="circle"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                handleToggle()
              }}
            >
              {getToggleIcon()}
            </Button>
          </>
        )}
      </div>
      {direction === 'vertical' ? (
        // CSS Grid approach for smooth height animation with auto content
        <div
          className="grid transition-[grid-template-rows,opacity] duration-300 ease-in-out"
          style={{
            gridTemplateRows: isOpen ? '1fr' : '0fr',
            opacity: isOpen ? 1 : 0,
          }}
        >
          <div className={isOpen ? 'overflow-visible' : 'overflow-hidden'}>
            <div
              ref={contentRef}
              className={`${showBorder ? 'border-t border-border' : ''} p-4 ${contentClassName}`}
            >
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div
          className="overflow-visible transition-all duration-300 ease-in-out"
          style={{
            width: isOpen ? '100%' : 0,
            opacity: isOpen ? 1 : 0,
          }}
        >
          <div ref={contentRef} className={`p-4 ${contentClassName}`}>
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
